import React, { useState, useEffect, useRef, Fragment } from 'react';
import Message from '../components/Message.jsx';
import Typing from '../components/TypingIndicator.jsx';
import Header from '../components/Header.jsx';
import Input from '../components/Input.jsx';
import axios from '../utils/axiosConfig';
import Report from '../Report.jsx';
import Menu from '../components/Menu.jsx';
import MessageMenu from '../components/MessageMenu.jsx';
import Sources from '../components/Sources.jsx';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { analytic } from '../utils/Analytics.js';
import PromptsSuggestion from '../components/PromptsSuggestion.jsx';
import ConfirmationDialog from '../components/ConfirmationDialog.jsx';
import WidgetFormModal from '../components/WidgetFormModal.jsx';
import Loader from '../components/Loader.jsx';
import useWebSocket from 'react-use-websocket';
import LanguageSelection from '../components/LanguageSelection.jsx';
import VoiceMode from '../components/VoiceMode.jsx';
import useVoiceMode from '../hooks/useVoiceMode.js';
import useRealtimeVoice from '../hooks/useRealtimeVoice.js';
import useMessageFeedback from '../hooks/useMessageFeedback.js';
import { Helmet } from 'react-helmet';
import useAudioTranscriptionStore from '../hooks/useAudioTranscription.js';
import useFileAttachmentsStore from '../hooks/useFileAttachments.js';
import ChatProviders from '../context/ChatProviders.jsx';

const ChatWindow = ({ botResponse, iframeDomain, botApiId, primaryColor, view, showDisclaimer, showVoiceMode, publicMode, conversationId, theme, previewMode }) => {
    const { t, i18n } = useTranslation();

    let site = window.location.origin;
    let assetUrl = process.env.REACT_APP_ASSETS_URL;
    const apiURL = process.env.REACT_APP_API_URL;
    const apiKey = process.env.REACT_APP_BOT_API_KEY;
    const websocketURL = process.env.REACT_APP_WEBSOCKET_URL || "bot.brainstormer.dev";
    const SAMPLE_RATE = process.env.REACT_APP_SAMPLE_RATE || 44100;
    const THRESHOLD = process.env.REACT_APP_THRESHOLD || 3;
    const PROMPT_COUNT = process.env.REACT_APP_PROMPT_COUNT || 4;

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const showHeader = urlParams?.get('header') ? urlParams?.get('header') === 'true' : true;
    const showClearConversation = urlParams?.get('clear') ? urlParams?.get('clear') === 'true' : true;
    const signature = urlParams?.get('signature') || null;
    const timestamp = urlParams?.get('timestamp') || null;
    const key = urlParams?.get('org_key') || null;
    const assistance = urlParams?.get('assistance') || null;
    const orgId = urlParams?.get('org') || null;

    const [messages, setMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [domain, setDomain] = useState(iframeDomain);
    const [botId, setBotId] = useState(botApiId);
    const [bot, setBot] = useState({
        id: '',
        name: '',
        description: "",
        slug: '',
        image: `${site}/background.png`
    });
    const [primary, setPrimary] = useState(primaryColor);
    const [disabled, setDisabled] = useState(true);
    const [noWelcomeMessage, setNoWelcomeMessage] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [showHeaderMenu, setShowHeaderMenu] = useState(false);
    const [showMessageMenu, setShowMessageMenu] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const languages = [
        { id: 'english', name: 'English', value: 'en' },
        { id: 'arabic', name: 'عربي', value: 'ar' },
        { id: 'french', name: 'Française', value: 'fr' },
        { id: 'hindi', name: 'हिंदी', value: 'hi' },
        { id: 'spanish', name: 'Español', value: 'es' },
        { id: 'russian', name: 'русский', value: 'ru' },
        { id: 'german', name: 'Deutsch', value: 'de' },
        { id: 'swahili', name: 'Kiswahili', value: 'sw' }
    ];
    const [botLanguages, setBotLanguages] = useState([]);

    const [showSources, setShowSources] = useState(false);
    const [showPrompts, setShowPrompts] = useState(false);
    const [starterPoints, setStarterPoints] = useState();
    const [showLanguageDialog, setShowLanguageDialog] = useState(false);
    const [showClearConvoDialog, setShowClearConvoDialog] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [loader, setLoader] = useState(false);
    const [messageStreaming, setMessageStreaming] = useState(false);
    const [promptsLoading, setPromptsLoading] = useState(false);
    const [promptsErr, setPromptsErr] = useState(false);
    const [messageSendTime, setMessageSendTime] = useState(null);
    const [lastUserMessage, setLastUserMessage] = useState(null);
    const [isRealtime, setIsRealtime] = useState(false);
    const [messageFeedbackState, setMessageFeedbackState] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [formFields, setFormFields] = useState([]);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [messageCount, setMessageCount] = useState(0);
    const [formTriggerAfter, setFormTriggerAfter] = useState(3);
    const textAreaRef = useRef();
    const messageListRef = useRef();
    const didMount = useRef(false);
    const locationDataRef = useRef(null);

    const audioTranscription = useAudioTranscriptionStore({ textAreaRef, botId, orgId, t });
    const {
        setVoiceNotesEnabled,
        setSttModel,
        setSttProvider,
    } = audioTranscription;

    const fileAttachments = useFileAttachmentsStore({ conversationId, botId, orgId, t });
    const {
        attachments,
        attachmentsUploadResponse,
        clearAttachments,
        setFileSharingEnabled,
        setFileSizeLimitBytes,
        setAllowedFileTypes,
    } = fileAttachments;

    const { submitFeedback } = useMessageFeedback({
        normalizedBotId: botId,
        orgId,
    });

    let langToast;

    let chatID = conversationId || localStorage.getItem(`chatId_${botId}`);
    let initialLanguageSet = localStorage.getItem(`initialLanguageSet_${botId}`);

    const wsProtocol = websocketURL.includes('localhost') || websocketURL.includes('127.0.0.1') ? 'ws' : 'wss';
    const socketUrl = chatID ? `${wsProtocol}://${websocketURL}/socket/widget_handler/v2/${chatID}?token=${apiKey}` : null;

    const handleBotResponse = (response) => {
        if (previewMode !== null && previewMode && response?.data?.data[0]?.attributes?.is_published === true) {
            return;
        }

        let language = localStorage.getItem('language') || 'en';

        const isInitialLanguageSet = localStorage.getItem(`initialLanguageSet_${botId}`);

        let bot = response?.data?.data[0]?.attributes;
        let botImage = bot?.ProfileImage?.data?.attributes?.url;
        setIsRealtime(Boolean(bot?.RealtimeVoice_enabled));

        const injectWelcomeIfMissing = (history, welcomeText) => {
            if (!Array.isArray(history) || history.length === 0 || !welcomeText) {
                return history;
            }

            if (history[0].role === 'user') {
                return [
                    {
                        role: 'assistant',
                        content: welcomeText,
                        is_flagged: 0,
                        reason: '',
                        messaged_at: Date.now()
                    },
                    ...history
                ];
            }

            return history;
        };

        if (response?.data?.chat_id) {
            localStorage.setItem(`chatId_${botId}`, response?.data?.chat_id);
        }

        if (conversationId) {
            localStorage.setItem(`chatId_${botId}`, conversationId);
            localStorage.setItem(`initialLanguageSet_${botId}`, true);
        }

        voiceLegacy.setVoiceModeEnabled(Boolean(bot?.VoiceEnabled));
        voiceRealtime.setVoiceModeEnabled(Boolean(bot?.RealtimeVoice_enabled));

        if (language === 'en') {

            setBot({
                id: response?.data?.data[0]?.id || '',
                name: bot?.Name ? bot?.Name : "Brainstormer",
                description: bot?.Description ? bot?.Description : "",
                image: botImage ? `${assetUrl}${botImage}` : `${site}/App-icon.png`,
                slug: bot?.Slug ? bot?.Slug : ''
            })

        } else {

            let localizedData = bot?.localizations?.data?.find((lng) => lng?.attributes?.locale === language);

            setBot({
                id: response?.data?.data[0]?.id,
                name: localizedData?.attributes?.Name ? localizedData?.attributes?.Name : bot?.Name,
                description: localizedData?.attributes?.Description ? localizedData?.attributes?.Description : bot?.Description,
                image: botImage ? `${assetUrl}${botImage}` : `${site}/App-icon.png`,
                slug: localizedData?.attributes?.Slug ? localizedData?.attributes?.Slug : bot?.Slug
            })
        }

        if (isInitialLanguageSet) {
            if (language === 'en') {

                const englishWelcome = bot?.WelcomeMessage || 'Welcome my master. Your message is my command!';

                if (response?.data?.chat && response?.data?.chat?.chat_history && response?.data?.chat?.chat_history?.length > 0) {
                    setMessages(injectWelcomeIfMissing(response?.data?.chat?.chat_history, englishWelcome));
                } else {
                    setMessages([{ role: 'assistant', content: englishWelcome, is_flagged: 0, reason: '', messaged_at: Date.now() }]);
                }

                setStarterPoints(bot?.StarterPrompts);

            } else {

                let localizedData = bot?.localizations?.data?.find((lng) => lng?.attributes?.locale === language);
                const localizedWelcome = localizedData?.attributes?.WelcomeMessage ?? bot?.WelcomeMessage ?? 'Welcome my master. Your message is my command!';

                if (response?.data?.chat) {
                    setMessages(injectWelcomeIfMissing(response?.data?.chat?.chat_history, localizedWelcome));
                } else {
                    setMessages([{ role: 'assistant', content: localizedWelcome, is_flagged: 0, reason: '', messaged_at: Date.now() }]);
                }

                setStarterPoints(localizedData?.attributes?.StarterPrompts || bot?.StarterPrompts);
            }
        }

        setDisabled(false);

        let botLanguages = [];

        botLanguages.push(bot?.locale);

        bot?.localizations?.data?.map((lng) => {
            botLanguages.push(lng?.attributes?.locale);
        })

        if (botLanguages.length == 1 && !isInitialLanguageSet) {
            handleLanguageSelection(languages.filter((lng) => lng?.value == bot?.locale)[0]);
        }

        setBotLanguages(botLanguages);

        setFileSharingEnabled(bot?.file_sharing ?? false);

        if (bot?.file_size_limit_bytes) {
            setFileSizeLimitBytes(bot?.file_size_limit_bytes);
        }

        if (bot?.file_types?.length > 0) {
            setAllowedFileTypes(bot.file_types);
        }

        setVoiceNotesEnabled(bot?.enable_voice_notes || false);
        // voice notes STT config from bot attributes
        const modelData = bot?.voice_notes_model?.data;
        const providerData = bot?.voice_notes_provider?.data;
        setSttModel(typeof modelData === 'string' ? modelData : (modelData?.attributes?.AsrModel ?? ''));
        setSttProvider(typeof providerData === 'string' ? providerData : (providerData?.attributes?.Name ?? ''));
    }

    /**
     * Fetches location data from ipapi.co based on user's IP address.
     * Caches the result to avoid multiple API calls.
     * @returns {Promise<Object|null>} Location data object or null if fetch fails
     */
    const fetchLocationData = async () => {
        if (locationDataRef.current) {
            return locationDataRef.current;
        }

        try {
            const response = await fetch('https://ipapi.co/json/', {});
            if (response.ok) {
                const data = await response.json();
                locationDataRef.current = data;
                return data;
            }
        } catch (error) {
            console.error('Error fetching location data:', error);
            // Return null on error to allow the request to continue without location data
            return null;
        }

        return null;
    };

    /**
     * Fetches the chatbot's information and welcome message from the server.
     * If the chatbot has a welcome message, it is added to the messages array.
     * If the chatbot has starter prompts, they are stored in the starterPoints state.
     * The bot's languages are also stored in the botLanguages state.
     * If the language is not English, the localized data is fetched and stored in the bot state.
     * If the language is English, the English data is used.
     * @function
     */
    const fetchInfo = async () => {
        let chat_id = conversationId || localStorage.getItem(`chatId_${botId}`);
        let language = localStorage.getItem('language') || 'en';

        let requestData = {
            language: language || 'en',
            domain: domain
        }

        if (chat_id) {
            requestData.chat_id = chat_id
        }

        if (signature) {
            requestData.signature = signature;
        }

        if (timestamp) {
            requestData.timestamp = timestamp;
        }

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);

        const customVars = {};
        for (const [key, value] of urlParams.entries()) {
            if (key.startsWith('cv_')) {
                const decodedKey = decodeURIComponent(key.substring(3));
                const decodedValue = decodeURIComponent(value);
                customVars[decodedKey] = decodedValue;
            }
        }

        try {
            const locationData = await fetchLocationData();
            if (locationData) {
                const locationFields = [
                    'city',
                    'region',
                    'region_code',
                    'country',
                    'country_name',
                    'country_code',
                    'country_code_iso3',
                    'country_capital',
                    'country_tld',
                    'continent_code',
                    'latitude',
                    'longitude',
                    'timezone',
                    'country_calling_code',
                    'currency',
                    'currency_name'
                ];

                locationFields.forEach(field => {
                    if (locationData[field] !== undefined && locationData[field] !== null) {
                        customVars[field] = locationData[field];
                    }

                    if (locationData.timezone) {
                        requestData.timezone = locationData.timezone;
                    }
                });
            }
        } catch (error) {
            console.error('Error processing location data:', error);
        }

        if (Object.keys(customVars).length > 0) {
            requestData['custom_variables'] = customVars;
        }

        setLoader(true);

        axios({
            url: `${apiURL}/bot_by_id/bot_${botId}`,
            method: 'POST',
            data: requestData,
            headers: {
                'Api-token': apiKey,
                ...(key ? { 'Authorization': key } : {})
            }
        })
            .then((response) => {
                // console.log(response);
                if (response.status === 200 && response.data.data.length > 0) {
                    handleBotResponse(response);
                }
            })
            .catch((err) => {
                console.log(err);

                if (window && window.ReactNativeWebView) {

                    const error = {
                        eventType: 'error',
                        payload: {
                            statusCode: err.response.status,
                            message: err.response.data.message,
                        }
                    }

                    window.ReactNativeWebView.postMessage(JSON.stringify(error));

                    analytic("error_pushed_to_app", error);
                }

                toast.error(err.response.data.error || t('Something broke! Please try later.'), {
                    position: 'top-right',
                    style: {
                        backgroundColor: 'white',
                        color: 'red',
                        fontSize: '13px',
                        padding: '5px 7px',
                        fontWeight: '500'
                    }
                })
            })
            .finally(() => {
                setLoader(false);
            })
    };

    const { sendJsonMessage, readyState } = useWebSocket(socketUrl, {
        onOpen: (event) => handleSocketConnection(event),
        onMessage: (event) => handleSocketOnMessage(event),
        onError: (event) => handleSocketOnError(event),
        shouldReconnect: (closeEvent) => true,
        reconnectInterval: 100,
        retryOnError: true,
        heartbeat: {
            interval: 25000
        }
    });

    const voiceLegacy = useVoiceMode({
        sendJsonMessage,
        messages,
        setMessages,
        botId,
        languages,
        SAMPLE_RATE,
    });

    const voiceRealtime = useRealtimeVoice({
        botId,
        chatId: chatID,
        languages,
        messages,
        setMessages,
        // SAMPLE_RATE,
    });

    const voice = isRealtime ? voiceRealtime : voiceLegacy;

    const handleSocketConnection = (event) => {
        // console.log("new connection created", event)
    };

    const handleSocketOnMessage = (event) => {

        if (event.data == 'pong') return;

        const response = JSON.parse(event.data);

        if (response.status === "error") {
            setIsTyping(false);
            setMessageStreaming(false);

            if (voice.voiceMode) {
                voice.exitVoiceMode();
            }

            if (response?.message && response?.message.includes("Unsupported files")) {
                toast.error(t('Files are not supported with the current bot model. Please clear the conversation to start a new chat.'), {
                    position: 'top-right',
                    style: {
                        backgroundColor: 'white',
                        color: 'red',
                        fontSize: '13px',
                        padding: '5px 7px',
                        fontWeight: '500'
                    }
                });
            }
            else {
                toast.error(response?.message || t('Something broke! Please try again later'), {
                    position: 'top-right',
                    style: {
                        backgroundColor: 'white',
                        color: 'red',
                        fontSize: '13px',
                        padding: '5px 7px',
                        fontWeight: '500'
                    }
                });
            }

            return;
        };

        if (response.questions) {
            setPromptsLoading(false);
            setPromptsErr(false);
            setStarterPoints(response.questions);
            return;
        }

        if (response.question_error) {
            setPromptsLoading(false);
            setPromptsErr(true);
            setStarterPoints([]);
            return;
        }

        if (voice.handleVoiceSocketEvent(response)) {
            return;
        }

        const newMessage = {
            role: 'assistant',
            content: response.message,
            is_flagged: 0,
            reason: '',
            id: response.id,
            messaged_at: response.message_at
        };

        setMessages((prevMessages) => {
            const existingMessageIndex = prevMessages.findIndex(msg => msg.id === response.id);

            if (existingMessageIndex !== -1 && response?.data?.finish_reason === "stop") {
                return prevMessages;
            }

            if (prevMessages.length > 0 && prevMessages[prevMessages.length - 1].role === 'assistant' && response?.data?.finish_reason !== "stop" && response?.message !== "") {
                return [...prevMessages.slice(0, -1), {
                    ...prevMessages[prevMessages.length - 1],
                    content: prevMessages[prevMessages.length - 1].content + response.message,
                    id: response.id || prevMessages[prevMessages.length - 1].id,
                    messaged_at: response.message_at || prevMessages[prevMessages.length - 1].messaged_at
                }];
            } else {
                if (messageSendTime) {
                    const responseTime = (Date.now() - messageSendTime) / 1000;
                    console.log(`Bot response time: ${responseTime.toFixed(2)}s`);

                    const analyticsData = {
                        bot_id: botId,
                        bot_name: bot.name,
                        response_time_seconds: parseFloat(responseTime.toFixed(2)),
                        response_time_ms: Date.now() - messageSendTime,
                        user_message: lastUserMessage
                    };

                    analytic("response_time", analyticsData);
                    setMessageSendTime(null);
                    setLastUserMessage(null);
                }
                return [...prevMessages, newMessage];
            }
        });

        if (response?.data?.finish_reason === "stop") {
            sendJsonMessage({
                "event": "message_ack",
                "data": { "message_id": response?.id }
            })

            setMessageStreaming(false);
        }

        setIsTyping(false);

        if (response?.data?.finish_reason === "stop" && formFields.length > 0 && !formSubmitted) {
            setMessageCount(prev => {
                const newCount = prev + 1;
                if (chatID) {
                    localStorage.setItem(`formMsgCount_${botId}_${chatID}`, newCount.toString());
                }
                if (newCount >= formTriggerAfter) {
                    setShowForm(true);
                }
                return newCount;
            });
        }
    };

    const handleSocketOnError = (event) => {
        console.log("Error =====>", event);

        if (voice.voiceMode) {
            voice.exitVoiceMode();
        }
    };

    /**
     * Handle message send event. If the user is currently typing or the message is
     * being streamed, do nothing. Otherwise, set the user as typing, set the message
     * as streaming, add the message to the conversation log, and send the message
     * to the server. Analytics are also sent.
     *
     * @param {string} text The text of the message to send
     */
    const handleMessageSend = (text) => {
        if (isTyping || messageStreaming) return;

        setIsTyping(true);
        setMessageStreaming(true);

        setMessageSendTime(Date.now());
        setLastUserMessage(text);

        const outgoingAttachments = attachments.length
            ? attachments.map((attachment) => ({
                id: attachment.id,
                name: attachment.name,
                extension: attachment.extension,
                isImage: attachment.isImage,
                previewUrl: attachment.previewUrl,
                readableSize: attachment.readableSize,
                type: attachment.type,
                size: attachment.size,
                isLocalPreview: Boolean(attachment.previewUrl),
            }))
            : [];

        setMessages((prevMessage) => [
            ...prevMessage,
            {
                role: 'user',
                content: text,
                is_flagged: 0,
                reason: '',
                messaged_at: Date.now(),
                attachments: outgoingAttachments,
            },
        ]);
        sendMessage(text);

        handleSendUpdateToApp('user', text);

        if (attachments.length) {
            clearAttachments(false);
        }

        let data = {
            bot_name: bot.name
        }

        analytic("send_message", data);

        if (showPrompts) {
            setShowPrompts(false);
            setStarterPoints([]);
        }
    };

    const sendMessage = (text) => {

        const language = languages.find((lng) => lng.value === i18n.language);

        let requestData = {
            "event": "text_message",
            "data": {
                "query": text ? text : ' ',
                "bot_id": `bot_${botId}`,
                "org_id": orgId,
                "language": language?.name,
                "files": attachmentsUploadResponse?.data
            }
        }

        sendJsonMessage(requestData);
    };

    const scrollToEnd = () => {
        const messageList = messageListRef.current;
        messageList.scrollTop = messageList.scrollHeight;
    };

    const handleShowMenu = () => {
        setShowHeaderMenu(!showHeaderMenu);
    };

    const handleMessageMenu = (e, message, index) => {
        setShowMessageMenu(!showMessageMenu);
        if (message) {
            setSelectedMessage({ ...message, index: index });
        }
    };

    const handleMessageFeedbackStateChange = (messageKey, feedbackType) => {
        if (!messageKey) {
            return;
        }

        setMessageFeedbackState((prevState) => {
            if (!feedbackType) {
                const nextState = { ...prevState };
                delete nextState[messageKey];
                return nextState;
            }

            return {
                ...prevState,
                [messageKey]: feedbackType,
            };
        });
    };

    const handleSourceMenu = () => {
        setShowSources(!showSources);
    };

    const handlePromptsMenu = () => {
        setShowPrompts(!showPrompts);
        setShowHeaderMenu(false);
        setTimeout(() => {
            scrollToEnd();
        }, [200]);

        if (!showPrompts) {
            setPromptsLoading(true);
            setPromptsErr(false);

            const language = languages.find((lng) => lng.value === i18n.language);

            let data = {
                "event": "try_asking",
                "data": {
                    "bot_id": `bot_${botId}`,
                    "language": language?.name
                }
            }

            sendJsonMessage(data);
        } else {
            setStarterPoints([]);
        }

    };

    const handleOpenPrompts = () => {

        const assistantMessages = messages.filter(msg => msg.role === 'assistant');

        //add messageStreaming check in case of legacy support
        if (
            assistantMessages.length > 0 &&
            (assistantMessages.length % PROMPT_COUNT === 0 || assistantMessages.length == 1) &&
            theme == 'dark' &&
            messages[messages.length - 1].role === 'assistant'
        ) {
            if (!promptsLoading) {
                setPromptsLoading(true);

                const language = languages.find((lng) => lng.value === i18n.language);

                let data = {
                    "event": "try_asking",
                    "data": {
                        "bot_id": `bot_${botId}`,
                        "language": language?.name
                    }
                }

                sendJsonMessage(data);
            }

            setShowPrompts(true);
        }
    }

    const handlePromptClick = (text) => {
        handlePromptsMenu()
        handleMessageSend(text);
    };

    const handleLanguageClick = (language) => {
        setSelectedLanguage(language);
        setShowLanguageDialog(true);
        handleShowMenu();
    };

    const handleLanguageChange = () => {
        analytic("change_language", { language: selectedLanguage.value });

        localStorage.removeItem(`chatId_${botId}`);
        i18n.changeLanguage(selectedLanguage.value);
        localStorage.setItem('language', selectedLanguage.value);
        setMessages([]);
        setShowLanguageDialog(false);

        if (showVoiceMode && voice.voiceMode && voice.voiceModeEnabled) {
            voice.exitVoiceMode();
        }
    };

    const handleLanguageCancel = () => {
        setSelectedLanguage(null);
        setShowLanguageDialog(false);
    };

    const handleRefreshClick = () => {
        setShowClearConvoDialog(!showClearConvoDialog);
        setShowHeaderMenu(false);
    };

    /**
     * Handles confirmation of conversation clearing.
     * Removes chat ID and initial language set from local storage,
     * resets messages state, and hides the refresh confirmation dialog.
     */
    const handleRefreshConfirm = () => {
        const oldChatId = localStorage.getItem(`chatId_${botId}`);
        if (oldChatId) {
            localStorage.removeItem(`formSubmitted_${botId}_${oldChatId}`);
            localStorage.removeItem(`formMsgCount_${botId}_${oldChatId}`);
        }
        localStorage.removeItem(`chatId_${botId}`);
        localStorage.removeItem(`initialLanguageSet_${botId}`);
        setMessages([]);
        setFormSubmitted(false);
        setMessageCount(0);
        if (formTriggerAfter === 0) setShowForm(true);
        handleRefreshClick();

        if (showVoiceMode && voice.voiceMode && voice.voiceModeEnabled) {
            voice.exitVoiceMode();
        }
    };

    /**
     * Handles cancellation of conversation clearing.
     * Hides the refresh confirmation dialog.
     */
    const handleRefreshCancel = () => {
        handleRefreshClick();
    };

    /**
     * Toggles the width of the chat window by sending a message to the parent window.
     * After toggling, it scrolls to the end of the chat content.
     */
    const handleToggleWidth = () => {
        window.parent.postMessage({
            func: 'toggleWidth',
        }, `https://${domain}`);

        setTimeout(() => {
            scrollToEnd();
        }, 300)
    };

    const handleLanguageSelection = (language) => {
        i18n.changeLanguage(language?.value);
        localStorage.removeItem(`chatId_${botId}`);
        localStorage.setItem('language', language?.value);
        localStorage.setItem(`initialLanguageSet_${botId}`, true);
        fetchInfo();
    };

    const handleFormSubmit = async (formData) => {
        try {
            await fetch(`${apiURL}/widget/form-submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Api-token': apiKey },
                body: JSON.stringify({
                    bot_id: botId,
                    chat_id: chatID,
                    data: formData,
                }),
            });
        } catch (e) {
            console.error('Form submit failed', e);
        }
        setShowForm(false);
        setFormSubmitted(true);
        if (chatID) {
            localStorage.setItem(`formSubmitted_${botId}_${chatID}`, 'true');
            localStorage.removeItem(`formMsgCount_${botId}_${chatID}`);
        }
    };

    const handleSendUpdateToApp = (userType, message) => {
        if (!message) return;

        const shortMessage = message && message.substring(0, 50);

        const data = {
            user: userType,
            message: shortMessage,
            date: new Date().toISOString()
        }

        if (window && window.ReactNativeWebView) {
            const obj = {
                eventType: 'message',
                payload: data
            }

            window.ReactNativeWebView.postMessage(JSON.stringify(obj));

            analytic("last_message_pushed_to_app", obj);
        }
    }

    useEffect(() => {
        scrollToEnd();
    }, [messages, starterPoints]);

    useEffect(() => {
        if (messages.length == 0) {
            fetchInfo();
            // handleBotResponse(botResponse);
        }

        if (messages.length == 1) {
            setTimeout(() => {
                scrollToEnd();
            }, [200]);
        }

        if (!messageStreaming) {
            handleSendUpdateToApp('assistant', messages[messages.length - 1]?.content);
        }

    }, [messages]);

    useEffect(() => {
        handleOpenPrompts();
    }, [messages.length, messageStreaming]);

    useEffect(() => {
        if (primary) {
            document.documentElement.style.setProperty('--primary-color', primary);
        }
    }, [primary]);

    useEffect(() => {
        let data = {
            parent_domain: domain
        }

        analytic("app_invokation", data);

        const targetOrigin = domain && domain.includes('localhost') ? `http://${domain}` : `https://${domain}`;

        if (window.self !== window.top) {
            window.parent.postMessage({
                page: 'ready',
            }, targetOrigin);
        }
    }, []);

    useEffect(() => {
        if (!botId) return;
        fetch(`${apiURL}/widget/form-config/${botId}`, {
            headers: { 'Api-token': apiKey }
        })
            .then(r => r.json())
            .then(config => {
                if (config.enabled) {
                    setFormFields(config.fields || []);
                    const triggerAfter = config.trigger_after_messages || 0;
                    setFormTriggerAfter(triggerAfter);
                    const chatId = conversationId || localStorage.getItem(`chatId_${botId}`);
                    const alreadySubmitted = !!localStorage.getItem(`formSubmitted_${botId}_${chatId}`);
                    setFormSubmitted(alreadySubmitted);
                    const savedCount = chatId ? localStorage.getItem(`formMsgCount_${botId}_${chatId}`) : null;
                    if (savedCount) {
                        const count = parseInt(savedCount, 10);
                        setMessageCount(count);
                        if (!alreadySubmitted && count >= triggerAfter) {
                            setShowForm(true);
                        }
                    } else if (triggerAfter === 0 && !alreadySubmitted) {
                        setShowForm(true);
                    }
                }
            })
            .catch(() => {});
    }, [botId, apiURL, apiKey]);

    return (
        <ChatProviders
            audioTranscription={audioTranscription}
            fileAttachments={fileAttachments}
        >
            <Fragment>
                <Helmet>
                    <title>{bot?.name}</title>
                    <meta name="title" content={bot?.name} />
                    <meta name="description" content={bot?.description} />
                </Helmet>
                <div className="flex-1 justify-between flex flex-col h-[100dvh] relative">
                    {showHeader &&
                        <Header
                            bot={bot}
                            handleRefresh={handleRefreshClick}
                            primary={primary}
                            handleToggleWidth={handleToggleWidth}
                            view={view}
                            isIntialLanguageSelected={initialLanguageSet}
                            showVoiceMode={showVoiceMode && voice.voiceModeEnabled}
                            handleOpenVoiceMode={voice.openVoiceMode}
                            publicMode={publicMode}
                        />
                    }
                    <Menu
                        bot={bot}
                        domain={domain}
                        showHeaderMenu={showHeaderMenu}
                        setShowHeaderMenu={setShowHeaderMenu}
                        messages={messages}
                        handleShowMenu={handleShowMenu}
                        primary={primary}
                        languages={languages}
                        botLanguages={botLanguages}
                        fetchInfo={fetchInfo}
                        handleLanguageClick={handleLanguageClick}
                        handlePromptsMenu={handlePromptsMenu}
                        handleRefresh={handleRefreshClick}
                        promptsLoading={promptsLoading}
                        promptsErr={promptsErr}
                        prompts={starterPoints}
                        botId={botId}
                        voiceMode={showVoiceMode && voice.voiceModeEnabled}
                        publicMode={window.self == window.top}
                        chatId={chatID}
                        showClearConversation={showClearConversation}
                    />
                    <div id="message-list" ref={messageListRef} className="flex flex-col h-full py-1 pb-0 sm:py-6 overflow-y-auto justify-between">
                        {
                            (!initialLanguageSet && botLanguages.length > 0) ? (
                                <LanguageSelection
                                    primary={primary}
                                    botLanguages={botLanguages}
                                    languages={languages}
                                    handleLanguageSelection={handleLanguageSelection}
                                />
                            ) : (
                                <>
                                    <div id="messages-list-inside" className='w-auto h-auto px-[10px] pb-2'>
                                        {messages.map((message, index) => (
                                            <Message
                                                key={index}
                                                index={index}
                                                message={message}
                                                image={bot.image}
                                                primary={primary}
                                                setShowReport={setShowReport}
                                                handleMessageMenu={handleMessageMenu}
                                                messageStreaming={messageStreaming}
                                                messagedAt={message?.messaged_at}
                                                conversationId={chatID}
                                                messages={messages}
                                                submitFeedback={submitFeedback}
                                                feedbackState={messageFeedbackState[message?.id || `feedback-${index}`] || null}
                                                onFeedbackStateChange={handleMessageFeedbackStateChange}
                                            />
                                        ))}
                                        {isTyping &&
                                            <Typing
                                                image={bot.image}
                                                primary={primary}
                                            />
                                        }
                                    </div>
                                    <PromptsSuggestion
                                        promptsLoading={promptsLoading}
                                        promptsErr={promptsErr}
                                        data={starterPoints}
                                        showPrompts={showPrompts}
                                        setShowPrompts={setShowPrompts}
                                        handlePromptClick={handlePromptClick}
                                        primary={primary}
                                        theme={theme}
                                    />
                                </>
                            )
                        }
                    </div>
                    <Input
                        botName={bot.name}
                        textAreaRef={textAreaRef}
                        handleMessageSend={handleMessageSend}
                        isTyping={isTyping}
                        setIsTyping={setIsTyping}
                        messageStreaming={messageStreaming}
                        setMessageStreaming={setMessageStreaming}
                        disabled={disabled}
                        primary={primary}
                        handleShowMenu={handleShowMenu}
                        showDisclaimer={showDisclaimer}
                        isIntialLanguageSelected={initialLanguageSet}
                        chatAssistance={assistance == 'disabled' || assistance == "disable" || assistance == 'false' || theme == 'dark'}
                    />
                    <Report
                        showReport={showReport}
                        botId={botId}
                        setShowReport={setShowReport}
                        primary={primary}
                        fadeEffect={'zoomIn'}
                        selectedMessage={selectedMessage}
                        messages={messages}
                    />
                    <MessageMenu
                        bot={bot}
                        domain={domain}
                        showMessageMenu={showMessageMenu}
                        handleMessageMenu={handleMessageMenu}
                        primary={primary}
                        setShowReport={setShowReport}
                        showSources={showSources}
                        setShowSources={setShowSources}
                        handleSourceMenu={handleSourceMenu}
                        selectedMessage={selectedMessage}
                        botId={botId}
                        voiceMode={showVoiceMode && voice.voiceModeEnabled}
                        publicMode={window.self == window.top}
                        chatId={chatID}
                        chatAssistance={assistance == 'disabled' || assistance == "disable" || assistance == 'false' || theme == 'dark'}
                    />
                    <Sources
                        showSources={showSources}
                        setShowSources={setShowSources}
                        handleSourceMenu={handleSourceMenu}
                    />
                    <ConfirmationDialog
                        title={t(`Are you sure you want to change the language to `) + t(`${selectedLanguage?.id}`) + "?" + t(` Doing so will clear this conversation.`)}
                        showDialog={showLanguageDialog}
                        handleConfirm={handleLanguageChange}
                        handleCancel={handleLanguageCancel}
                        primary={primary}
                    />
                    <ConfirmationDialog
                        title={t('Are you sure you want to clear conversation?')}
                        showDialog={showClearConvoDialog}
                        handleConfirm={handleRefreshConfirm}
                        handleCancel={handleRefreshCancel}
                        primary={primary}
                    />
                    <Loader
                        primary={primary}
                        showLoader={loader}
                    />
                    <VoiceMode
                        botImage={bot.image}
                        botName={bot.name}
                        primary={primary}
                        voice={voice}
                    />
                    {showForm && (
                        <WidgetFormModal
                            fields={formFields}
                            onSubmit={handleFormSubmit}
                            onClose={() => setShowForm(false)}
                            primary={primary}
                            theme={theme}
                        />
                    )}
                    <Toaster
                        toastOptions={{
                            style: {
                                background: theme == 'dark' ? '#232326' : '#ffffff',
                                color: theme == 'dark' ? '#ffffff' : '#000000',
                            }
                        }}
                    />
                </div>
            </Fragment>
        </ChatProviders>
    );
}

export default ChatWindow;
